/**
 * Shared display + parsing helpers for the mobile screens.
 *
 * Lightweight, dependency-free date/time handling: the app uses plain Field text
 * inputs with explicit ISO-ish formatting rather than adding @react-native-community/
 * datetimepicker (not a current dependency — see WO note). All formatting is locale
 * via Intl, which ships with the RN runtime.
 */

import { ApiRequestError } from '../api/client';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export function extractErrorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

export function formatCurrency(value: string | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

// ---------------------------------------------------------------------------
// Quantities
// ---------------------------------------------------------------------------

/**
 * Quantity display. Backend Decimal quantities serialize with 4 trailing decimals
 * ("1.0000", "0.7500"); that granularity is noise on screen, so render a consistent
 * 2-decimal value ("1.00", "0.75"). Accepts a string or number; returns the raw input
 * unchanged when it isn't numeric so we never show a misleading rounded value.
 */
export function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return String(value);
  return num.toFixed(2);
}

// ---------------------------------------------------------------------------
// Dates / times (all inputs are ISO strings or null)
// ---------------------------------------------------------------------------

/** "Mon, Jun 16" — used for agenda day headers and event/task dates. */
export function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "9:30 AM" */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** "Jun 16, 9:30 AM" — compact date+time for rows/detail. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** YYYY-MM-DD date key for grouping events by day in local time. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Relative due label + overdue flag. Returns { text, overdue }.
 * "Overdue" / "Due today" / "Tomorrow" / "in 3d" / "Jun 20".
 */
export function formatDueRelative(iso: string | null | undefined): { text: string; overdue: boolean } {
  if (!iso) return { text: 'No due date', overdue: false };
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return { text: iso, overdue: false };
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDue = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
  const dayMs = 86_400_000;
  const diffDays = Math.round((startOfDue - startOfToday) / dayMs);

  if (due.getTime() < now.getTime() && diffDays <= 0) {
    return { text: diffDays < 0 ? `Overdue ${Math.abs(diffDays)}d` : 'Overdue', overdue: true };
  }
  if (diffDays === 0) return { text: 'Due today', overdue: false };
  if (diffDays === 1) return { text: 'Due tomorrow', overdue: false };
  if (diffDays > 1 && diffDays <= 7) return { text: `Due in ${diffDays}d`, overdue: false };
  return {
    text: `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    overdue: false,
  };
}

// ---------------------------------------------------------------------------
// ISO range helpers for the calendar agenda window
// ---------------------------------------------------------------------------

export function startOfTodayIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
}

export function isoDaysFromNow(days: number): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Datetime text-input parsing (lightweight, no datetimepicker dep)
//   Accepts "YYYY-MM-DD" or "YYYY-MM-DD HH:MM" (24h) and returns an ISO string,
//   or null if the field is blank. Throws on an unparseable non-blank value so
//   the form can surface an inline error.
// ---------------------------------------------------------------------------

export function parseDateTimeInput(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Normalize "YYYY-MM-DD HH:MM" → "YYYY-MM-DDTHH:MM" for Date parsing.
  const normalized = /^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}$/.test(trimmed)
    ? trimmed.replace(/\s+/, 'T')
    : trimmed;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Use YYYY-MM-DD or YYYY-MM-DD HH:MM (got "${trimmed}").`);
  }
  return d.toISOString();
}

/** ISO → "YYYY-MM-DD HH:MM" for pre-filling a datetime Field on edit. */
export function isoToInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Humanize a raw enum/status/type value for display (sentence case, matching the badge labels):
 * 'in_progress' → 'In progress', 'material' → 'Material', 'sent_for_signature' → 'Sent for signature'.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const s = value.replace(/[_-]+/g, ' ').trim();
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}
